import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  List,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCircleCheck,
  IconCircleX,
  IconCircleMinus,
  IconReload,
  IconBrandWhatsapp,
  IconSend,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { trpc } from '../../lib/trpc';

const STATUS_META: Record<string, { color: string; icon: React.ReactNode }> = {
  pass: { color: 'green', icon: <IconCircleCheck size={18} /> },
  passed: { color: 'green', icon: <IconCircleCheck size={18} /> },
  fail: { color: 'red', icon: <IconCircleX size={18} /> },
  failed: { color: 'red', icon: <IconCircleX size={18} /> },
  skip: { color: 'gray', icon: <IconCircleMinus size={18} /> },
  skipped: { color: 'gray', icon: <IconCircleMinus size={18} /> },
  warning: { color: 'orange', icon: <IconCircleMinus size={18} /> },
};

function ConnectionTest() {
  const [channel, setChannel] = useState<'whatsapp' | 'facebook'>('whatsapp');
  const [phoneNumber, setPhoneNumber] = useState('');

  const probe = trpc.analytics.connectionTest.run.useQuery(
    { channel },
    {
      refetchOnWindowFocus: false,
    },
  );

  const sendTest = trpc.integrations.meta.sendWhatsAppTestMessage.useMutation({
    onSuccess: ({ wamid }) => {
      notifications.show({
        title: 'Success',
        message: `Test message sent! wamid: ${wamid}`,
        color: 'green',
      });
      void probe.refetch();
    },
    onError: (err) => {
      notifications.show({
        title: 'Failed',
        message: err.message,
        color: 'red',
      });
    },
  });

  const handleSendTestMessage = () => {
    if (!phoneNumber.trim()) return;
    sendTest.mutate({ to: phoneNumber.trim() });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Connection test</Title>
          <Button
            variant="light"
            leftSection={<IconReload size={14} />}
            loading={probe.isFetching}
            onClick={() => probe.refetch()}
          >
            Run again
          </Button>
        </Group>

        <SegmentedControl
          value={channel}
          onChange={(value) => setChannel(value as 'whatsapp' | 'facebook')}
          data={[
            { label: 'WhatsApp', value: 'whatsapp' },
            { label: 'Facebook / Instagram', value: 'facebook' },
          ]}
        />

        {channel === 'whatsapp' ? (
          <Alert color="blue" icon={<IconBrandWhatsapp size={18} />}>
            5-step probe: App Secret fingerprint → Webhook verify token → WhatsApp connection → App
            Review → Recent inbound.
          </Alert>
        ) : (
          <Alert color="blue">
            5-step probe: token decrypt → Graph `/me` → `/me/accounts` → Page subscribed_fields →
            WABA subscribed_apps. Steps that have no source data yet (e.g. no connected Page) report{' '}
            <strong>skip</strong>.
          </Alert>
        )}

        <Card>
          {probe.isPending ? (
            <Skeleton height={200} />
          ) : (
            <List spacing="sm" size="sm" center>
              {(probe.data?.steps ?? []).map((s) => {
                const meta = (STATUS_META[s.status] ?? {
                  color: 'gray',
                  icon: <IconCircleMinus size={18} />,
                }) as { color: string; icon: React.ReactNode };
                return (
                  <List.Item
                    key={s.key}
                    icon={
                      <span style={{ color: `var(--mantine-color-${meta.color}-6)` }}>
                        {meta.icon}
                      </span>
                    }
                  >
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={500}>{s.label}</Text>
                      <Badge color={meta.color} variant="light" size="xs">
                        {s.status}
                      </Badge>
                    </Group>
                    {s.detail && (
                      <Text size="xs" c="dimmed">
                        {s.detail}
                      </Text>
                    )}
                  </List.Item>
                );
              })}
            </List>
          )}
        </Card>

        {channel === 'whatsapp' && (
          <Card withBorder>
            <Stack gap="sm">
              <Group gap="xs">
                <IconBrandWhatsapp size={18} />
                <Text fw={600}>Send test message</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Sends the pre-approved <code>hello_world</code> template to verify your connection
                end-to-end. This is the simplest way to prove that your credentials, WABA account,
                and Meta APIs are fully operational.
              </Text>
              <Group align="flex-end" wrap="nowrap" mt="xs">
                <TextInput
                  flex={1}
                  label="Recipient phone number"
                  description="E.164 format — e.g. +14155550100"
                  placeholder="+14155550100"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                />
                <Button
                  loading={sendTest.isPending}
                  disabled={phoneNumber.trim().length < 8}
                  onClick={handleSendTestMessage}
                  leftSection={<IconSend size={14} />}
                  mb={2}
                >
                  Send
                </Button>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/connection-test')({ component: ConnectionTest });
